import ExpiredLink from '@/components/expired-link';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { verifyEmailMutationFn } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import {
    Link,
    useNavigate,
    useOutletContext,
    useSearchParams,
} from 'react-router-dom';

export default function ConfirmAccount() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token');

    const exp = Number(params.get('exp'));
    const now = Date.now();
    const isExpired = token && exp && exp <= now;

    const queryClient = useQueryClient();
    const { userId } = useOutletContext<{ userId: string }>();

    const { mutate, isPending } = useMutation({
        mutationFn: verifyEmailMutationFn,
    });

    const handleSubmit = (e: { preventDefault: () => void }) => {
        const jwtRegex =
            /^([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_\-\\+\\/=]*)/gi;
        e.preventDefault();
        if (!token || !jwtRegex.test(token as string)) {
            toast({
                title: 'Error',
                description: 'Verification token not found',
                variant: 'destructive',
            });
            return;
        }
        mutate(
            { token, ...(userId && { userId }) },

            {
                onSuccess: (response) => {
                    toast({
                        title: 'Success',
                        description: response.data.message,
                        variant: 'default',
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['authUser'],
                    });
                    navigate(`/${userId ? 'settings' : 'login'}`, {
                        replace: true,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        description: error.message,
                        variant: 'destructive',
                    });
                },
            },
        );
    };
    return (
        <main className='max-w-lg min-h-[590px] h-full flex items-center justify-center mx-auto'>
            {isExpired ? (
                <ExpiredLink
                    title={`Invalid or expired ${userId ? 'recovery ' : ''} email verify link`}
                    message={
                        userId
                            ? 'You can send a recovery email change request again'
                            : 'You can log in again to send a new email verify request'
                    }
                    url={userId ? '/settings' : '/login'}
                    btnTitle={userId ? 'Go to settings' : 'Go to login'}
                />
            ) : (
                <div className='w-full h-full p-5 rounded-md'>
                    <Logo size='100px' />

                    <h1
                        className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-2 mt-8
        text-center'>
                        Email Verification
                    </h1>
                    <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                        To confirm your email, please click the button below.
                    </p>
                    <form
                        onSubmit={handleSubmit}
                        className='w-full flex items-center'>
                        <Button
                            disabled={isPending}
                            type='submit'
                            className='w-full mx-auto text-[15px] h-[40px] text-white font-semibold'>
                            {isPending && <Loader className='animate-spin' />}
                            Verify Email
                        </Button>
                    </form>

                    <p className='mt-6 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                        If you have any issue confirming your account please{' '}
                        <Link
                            target='_blank'
                            rel='noreferrer'
                            className='outline-none transition duration-150 ease-in-out 
            focus-visible:ring-2 text-primary hover:underline focus-visible:ring-primary font-semibold underline-offset-4'
                            to='//x.com/_isCoding'>
                            Contact us
                        </Link>
                        .
                    </p>
                </div>
            )}
        </main>
    );
}
